"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { RssIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGlobalData } from "@/components/TinaContext";
import { cn } from "@/utils";

interface RSSDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rssUrl?: string;
}

export function RssSubscribeButton({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="link"
      className={cn(
        "!text-base hover:!decoration-secondary-500 hover:!text-secondary-500",
        className
      )}
      onClick={onClick}
    >
      <RssIcon className="size-5 text-secondary-500" />
      Stay up to date
    </Button>
  );
}

export function RSSDialog({ open, onOpenChange, rssUrl }: RSSDialogProps) {
  const globalData = useGlobalData();
  const rssDialogTitle = globalData?.rssDialog?.title;
  const rssDialogDescription = globalData?.rssDialog?.description;
  const [isCopied, setIsCopied] = React.useState(false);

  const handleCopyLink = async () => {
    if (!rssUrl) return;

    try {
      await navigator.clipboard.writeText(rssUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy RSS link:", err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-2xl text-white">
        <DialogHeader className="pt-1">
          <DialogTitle className="text-2xl md:text-3xl font-normal text-white text-left">
            {rssDialogTitle}
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-base text-left pt-2">
            {rssDialogDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="pt-1">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
            <Input
              value={rssUrl || ""}
              readOnly
              className="flex-1 bg-gray-800 border-gray-700 text-gray-300 h-12 md:h-14 rounded-lg px-4 cursor-default focus-visible:ring-0 focus-visible:border-gray-700"
            />
            <Button
              onClick={handleCopyLink}
              variant="outline"
              className="w-full sm:w-auto sm:min-w-32"
            >
              {isCopied ? "Copied!" : "Copy Link"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
